/**
 * useListManager.ts - React hook for list management state
 * 
 * CHANGE HISTORY:
 * - 251130_1628 danielbeer: Created hook for ListManager state management
 * 
 * This hook encapsulates all state management logic for the ListManager UI,
 * delegating data operations to OrganizrManager APIs.
 */

import { useState, useEffect, useCallback } from 'react';
import type { ListData, ListItem, LoadListRegistryOptions } from '../types';
import { sanitizeListRegistry } from '../utils/listDataSanitizer';

export interface UseListManagerOptions {
  organizr: any; // OrganizrManager instance
  autoLoadRegistry?: boolean;
  onError?: (error: string) => void;
}

export interface UseListManagerReturn {
  // State
  availableLists: ListData[];
  selectedListId: string | null;
  currentList: ListData | null;
  outlinerNodes: any[];
  loading: boolean;
  error: string | null;
  hasUnsavedChanges: boolean;
  
  // Actions
  loadListRegistry: (options?: LoadListRegistryOptions) => Promise<void>;
  loadList: (listId: string) => Promise<void>;
  saveList: () => Promise<void>;
  selectList: (listId: string) => void;
  updateNodes: (nodes: any[]) => void;
  exportList: () => string;
  importList: (jsonString: string) => void;
  revertChanges: () => void;
  clearError: () => void;
}

/**
 * Hook for managing list state and operations
 * 
 * @param options - Hook options
 * @returns List manager state and actions
 * 
 * @example
 * const listManager = useListManager({
 *   organizr: getOrganizr(),
 *   autoLoadRegistry: true,
 *   onError: (err) => console.error(err)
 * });
 */
export function useListManager(options: UseListManagerOptions): UseListManagerReturn {
  const { organizr, autoLoadRegistry = true, onError } = options;
  
  // State
  const [availableLists, setAvailableLists] = useState<ListData[]>([]);
  const [selectedListId, setSelectedListId] = useState<string | null>(null);
  const [currentList, setCurrentList] = useState<ListData | null>(null);
  const [originalList, setOriginalList] = useState<ListData | null>(null);
  const [outlinerNodes, setOutlinerNodes] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  /**
   * Handle errors consistently
   */
  const handleError = useCallback((err: any, context: string) => {
    const errorMessage = `${context}: ${err instanceof Error ? err.message : String(err)}`;
    setError(errorMessage);
    if (onError) {
      onError(errorMessage);
    }
  }, [onError]);
  
  /**
   * Load list registry from API
   */
  const loadListRegistry = useCallback(async (opts?: LoadListRegistryOptions) => {
    try {
      setLoading(true);
      setError(null);
      
      const lists = await organizr.loadListRegistry({
        includeItemCounts: true,
        ...opts
      });
      
      // Sanitize lists with comprehensive validation and logging
      const { sanitized, totalWarnings, totalErrors } = sanitizeListRegistry(lists);
      
      if (totalErrors > 0) {
        console.error(`[useListManager] ${totalErrors} errors during sanitization - check console for details`);
      }
      
      setAvailableLists(sanitized);
      
      // Auto-select first list if none selected
      if (lists.length > 0 && !selectedListId) {
        setSelectedListId(lists[0].id);
      }
    } catch (err) {
      handleError(err, 'Failed to load list registry');
    } finally {
      setLoading(false);
    }
  }, [organizr, selectedListId, handleError]);
  
  /**
   * Load a single list from API
   */
  const loadList = useCallback(async (listId: string) => {
    try {
      console.log('[useListManager] Loading list:', listId);
      setLoading(true);
      setError(null);
      
      const listData = await organizr.loadListFromAPI(listId);
      console.log('[useListManager] Loaded list data:', listData);
      setCurrentList(listData);
      setOriginalList(JSON.parse(JSON.stringify(listData))); // Deep clone
      setHasUnsavedChanges(false);
      
      // Convert to Outlinr format
      const nodes = organizr.flattenToOutlinerNodes(listData.children || []);
      console.log('[useListManager] Converted to nodes:', nodes.length, 'nodes');
      setOutlinerNodes(nodes);
    } catch (err) {
      console.error('[useListManager] Error loading list:', err);
      handleError(err, `Failed to load list ${listId}`);
    } finally {
      setLoading(false);
    }
  }, [organizr, handleError]);
  
  /**
   * Save current list to API
   */
  const saveList = useCallback(async () => {
    if (!currentList) {
      handleError(new Error('No list selected'), 'Save failed');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Convert nodes back to list items
      const items = organizr.fromOutlinerNodes(
        outlinerNodes,
        currentList.color,
        currentList.pattern
      );
      
      // Update list with new items
      const updatedList: ListData = {
        ...currentList,
        children: items,
        lastUpdated: new Date().toISOString()
      };
      
      // Save to API
      const result = await organizr.saveListToAPI(currentList.id, updatedList);
      
      if (result.success) {
        setCurrentList(updatedList);
        setOriginalList(JSON.parse(JSON.stringify(updatedList))); // Update original
        setHasUnsavedChanges(false);
        // Refresh registry to update item counts
        await loadListRegistry();
      } else {
        throw new Error(result.error || 'Save failed');
      }
    } catch (err) {
      handleError(err, 'Failed to save list');
    } finally {
      setLoading(false);
    }
  }, [currentList, outlinerNodes, organizr, handleError, loadListRegistry]);
  
  /**
   * Select a list and load its data
   */
  const selectList = useCallback((listId: string) => {
    console.log('[useListManager] Selecting list:', listId);
    setSelectedListId(listId);
    loadList(listId);
  }, [loadList]);
  
  /**
   * Update Outlinr nodes and mark as changed
   */
  const updateNodes = useCallback((nodes: any[]) => {
    setOutlinerNodes(nodes);
    setHasUnsavedChanges(true);
  }, []);
  
  /**
   * Export current list as JSON
   */
  const exportList = useCallback((): string => {
    if (!currentList) {
      throw new Error('No list selected');
    }
    
    // Convert nodes to items before export
    const items = organizr.fromOutlinerNodes(
      outlinerNodes,
      currentList.color,
      currentList.pattern
    );
    
    const exportData: ListData = {
      ...currentList,
      children: items,
      lastUpdated: new Date().toISOString()
    };
    
    return JSON.stringify(exportData, null, 2);
  }, [currentList, outlinerNodes, organizr]);
  
  /**
   * Import list from JSON
   */
  const importList = useCallback((jsonString: string) => {
    try {
      const importedList: ListData = JSON.parse(jsonString);
      
      // Validate basic structure
      if (!importedList.id || !importedList.name || !Array.isArray(importedList.children)) {
        throw new Error('Invalid list format');
      }
      
      setCurrentList(importedList);
      
      // Convert to Outlinr format
      const nodes = organizr.flattenToOutlinerNodes(importedList.children);
      setOutlinerNodes(nodes);
      
      setError(null);
    } catch (err) {
      handleError(err, 'Failed to import list');
    }
  }, [organizr, handleError]);
  
  /**
   * Revert changes to original list
   */
  const revertChanges = useCallback(() => {
    if (originalList) {
      setCurrentList(JSON.parse(JSON.stringify(originalList)));
      const nodes = organizr.flattenToOutlinerNodes(originalList.children || []);
      setOutlinerNodes(nodes);
      setHasUnsavedChanges(false);
    }
  }, [originalList, organizr]);
  
  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);
  
  // Auto-load registry on mount
  useEffect(() => {
    if (autoLoadRegistry) {
      loadListRegistry();
    }
  }, [autoLoadRegistry, loadListRegistry]);
  
  // Load list when selection changes
  useEffect(() => {
    if (selectedListId) {
      loadList(selectedListId);
    }
  }, [selectedListId, loadList]);
  
  return {
    // State
    availableLists,
    selectedListId,
    currentList,
    outlinerNodes,
    loading,
    error,
    hasUnsavedChanges,
    
    // Actions
    loadListRegistry,
    loadList,
    saveList,
    selectList,
    updateNodes,
    exportList,
    importList,
    revertChanges,
    clearError
  };
}
